import LMSClient from '@cfc/lms-sdk'
import { backoff } from '@cfc/exponential-backoff'
import { get, createType } from 'lib/middleware/api'
import { domains, ENVIRONMENT_SUBDOMAIN } from 'config/env'
import api from 'lib/api/lmsApi'
import apiV2 from 'lib/api/lmsApiV2'
import blobApi from 'lib/api/blobApi'
import { formatError } from 'utils/formatError'
import Auth0Manager from 'auth/utils/auth0Manager'
import { actions } from './reducer'
import selectors from './selectors'
import { SHOULD_SHOW_IN_MODAL } from './constants'
import { CREATECOURSEINSTANCESCHEMA } from './data'

const createCourseInstance = (courseInstance, programLmsKey, userContext) => {
  return async dispatch => {
    dispatch(actions.creatingCourseInstance())

    try {
      if (!courseInstance) throw new Error('Failed to create class')
      await CREATECOURSEINSTANCESCHEMA.validate(courseInstance)
      const response = await api.addClassToInstructor(
        courseInstance,
        userContext,
      )
      const course = selectors.attachLessonCountToUnits(response.data)
      await dispatch(
        actions.creatingCourseInstanceSuccess({
          courseInstance: {
            ...course,
            grade: courseInstance.grade,
          },
          programLmsKey,
        }),
      )
      // TODO: CreateCourse handleSubmit should look to redux for course
      // not returned data from this function
      return course
    } catch (error) {
      await dispatch(
        actions.creatingCourseInstanceError({
          error: formatError(error, SHOULD_SHOW_IN_MODAL),
        }),
      )
      throw error
    }
  }
}

const createCourseInstanceV2 = (courseInstance, programLmsKey, userContext) => {
  return async dispatch => {
    dispatch(actions.creatingCourseInstance())

    try {
      if (!courseInstance) throw new Error('Failed to create class')
      await CREATECOURSEINSTANCESCHEMA.validate(courseInstance)
      const response = await apiV2.addClassToInstructor(
        courseInstance,
        userContext,
      )
      const course = selectors.attachLessonCountToUnits(response.data)
      await dispatch(
        actions.creatingCourseInstanceSuccess({
          courseInstance: {
            ...course,
            grade: courseInstance.grade,
          },
          programLmsKey,
        }),
      )
      // TODO: CreateCourse handleSubmit should look to redux for course
      // not returned data from this function
      return course
    } catch (error) {
      await dispatch(
        actions.creatingCourseInstanceError({
          error: formatError(error, SHOULD_SHOW_IN_MODAL),
        }),
      )
      throw error
    }
  }
}

const makeAttemptV2 = ({
  registrationId,
  leafNodeId,
  progress,
  periodProgress,
  scormProgress,
  userContextB64,
}) => {
  return async dispatch => {
    dispatch(actions.makingAttempt())

    try {
      const client = new LMSClient({
        environment: ENVIRONMENT_SUBDOMAIN,
      })
      const token = await Auth0Manager.getAccessToken()
      const response = (await client.makeV2Attempt({
        jwt: token,
        registrationId,
        leafNodeId,
        progress,
        periodProgress,
        scormProgress,
        userContextB64,
      })) || { data: 'mocked response' }
      const { data } = response || {}
      await dispatch(
        actions.makingAttemptSuccess({
          attempt: data,
        }),
      )
    } catch (error) {
      await dispatch(
        actions.makingAttemptError({
          error: formatError(error),
        }),
      )
      throw error
    }
  }
}

const makeAttemptV2Direct = ({ registrationId, leafNodeId, progress }) => {
  return async dispatch => {
    dispatch(actions.makingAttempt())

    try {
      const response = await apiV2.makeAttemptDirect({
        registrationId,
        leafNodeId,
        progress,
      })
      const { data } = response || {}
      await dispatch(
        actions.makingAttemptSuccess({
          attempt: data,
        }),
      )
    } catch (error) {
      await dispatch(
        actions.makingAttemptError({
          error: formatError(error),
        }),
      )
      throw error
    }
  }
}

const createSelaPathInstance = () => {
  return async (dispatch, getState) => {
    const isCreatingClass = getState().lmsManager?.isCreating
    if (isCreatingClass) return

    dispatch(actions.creatingCourseInstance())

    try {
      const response = await api.createSelaClass()
      const { data } = response || {}
      await dispatch(
        actions.creatingCourseInstanceSuccess({
          courseInstance: data,
          programLmsKey: 'Sela',
        }),
      )
    } catch (error) {
      await dispatch(
        actions.creatingCourseInstanceError({
          error: formatError(error),
        }),
      )
      throw error
    }
  }
}

const createFTPathInstance = () => {
  return async (dispatch, getState) => {
    const isCreatingClass = getState().lmsManager?.isCreating
    if (isCreatingClass) return

    dispatch(actions.creatingCourseInstance())

    try {
      const response = await api.createFTClass()
      const { data } = response || {}
      await dispatch(
        actions.creatingCourseInstanceSuccess({
          courseInstance: data,
          programLmsKey: 'SelaFT',
        }),
      )
    } catch (error) {
      await dispatch(
        actions.creatingCourseInstanceError({ error: formatError(error) }),
      )
      throw error
    }
  }
}

const deleteCourseInstance = courseInstance => {
  return async dispatch => {
    dispatch(actions.deletingCourseInstance())

    try {
      await api.deleteClass(courseInstance)
      await dispatch(actions.deletingCourseInstanceSuccess())
    } catch (error) {
      await dispatch(
        actions.deletingCourseInstanceError({
          error: formatError(error, SHOULD_SHOW_IN_MODAL),
        }),
      )
      throw error
    }
  }
}

const deleteCourseInstanceV2 = courseInstance => {
  return async dispatch => {
    dispatch(actions.deletingCourseInstance())

    try {
      await apiV2.deleteClass(courseInstance)
      await dispatch(actions.deletingCourseInstanceSuccess())
    } catch (error) {
      await dispatch(
        actions.deletingCourseInstanceError({
          error: formatError(error, SHOULD_SHOW_IN_MODAL),
        }),
      )
      throw error
    }
  }
}

const finishCourseInstanceV2 = registrationId => {
  return async dispatch => {
    dispatch(actions.finishingCourseInstance())

    try {
      await apiV2.finishRegistration(registrationId)
      await dispatch(actions.finishingCourseInstanceSuccess())
    } catch (error) {
      await dispatch(
        actions.finishingCourseInstanceError({
          error: formatError(error, SHOULD_SHOW_IN_MODAL),
        }),
      )
      throw error
    }
  }
}

const deletingCourseCreatedStatus = () => async dispatch =>
  dispatch(actions.deletingCourseCreatedStatus())

//  =======================================================

// This function uses apiMiddleware and it removes the need to create actions and you also specific the url directly removing the need for the lmsApi file
// this points to the dummy route
const fetchOrCreateInstructorProgramFlex = (siteId, userContext) => {
  return async (dispatch, getState) => {
    const state = getState()

    // Should not fetch if already fetching
    if (state?.lmsManager?.isFetchingOrCreatingProgramFlex) return

    dispatch(actions.fetchingOrCreatingInstructorProgramFlex())
    try {
      const response = await backoff(
        () => apiV2.fetchOrCreateInstructorProgramFlex(siteId, userContext),
        3,
      )
      await dispatch(
        actions.fetchingOrCreatingInstructorProgramFlexSuccess(response.data),
      )
    } catch (error) {
      await dispatch(
        actions.fetchingOrCreatingInstructorProgramFlexError({
          error: formatError(error),
        }),
      )
    }
  }
}

/**
 * Function that uses a GET request to fetch instructor data
 * Uses the same Redux actions as fetchOrCreateInstructorProgramFlex to maintain compatibility
 */
const fetchInstructorProgramFlexGet = () => {
  return async (dispatch, getState) => {
    const state = getState()

    // Should not fetch if already fetching
    if (state?.lmsManager?.isFetchingOrCreatingProgramFlex) return

    dispatch(actions.fetchingOrCreatingInstructorProgramFlex())
    try {
      const response = await backoff(
        () => apiV2.fetchInstructorProgramFlexGet(),
        3,
      )
      await dispatch(
        actions.fetchingOrCreatingInstructorProgramFlexSuccess(response.data),
      )
    } catch (error) {
      await dispatch(
        actions.fetchingOrCreatingInstructorProgramFlexError({
          error: formatError(error),
        }),
      )
    }
  }
}

/**
 * Creates a new class using the course-based LMS v2 instructor/me API
 * @param {Object} params - Creation parameters
 * @param {string} params.courseCode - LMS course code
 * @param {string} params.contentGroupCode - Content group code (e.g. 'dwb')
 * @param {string|number} params.siteId - Site ID
 * @param {string} params.classTitle - Class title
 */
const createCourseInstanceByCourse = ({
  courseCode,
  contentGroupCode,
  siteId,
  classTitle,
}) => {
  return async (dispatch, getState) => {
    const state = getState()

    if (state?.lmsManager?.isCreatingProgramFlex) return

    dispatch(actions.creatingCourseInstanceV2ProgramFlex())
    try {
      const response = await backoff(
        () =>
          apiV2.fetchOrCreateInstructorByCourse({
            courseCode,
            contentGroupCode,
            siteId,
            classTitle,
          }),
        3,
      )

      await dispatch(actions.creatingCourseInstanceV2ProgramFlexSuccess())
      await dispatch(fetchInstructorProgramFlexGet())

      return response.data
    } catch (error) {
      await dispatch(
        actions.creatingCourseInstanceV2ProgramFlexError({
          error: formatError(error),
        }),
      )
      throw error
    }
  }
}

/**
 * Creates a new class using the lms v2 instructor/me api and then fetches updated instructor data
 * @param {Object} userContext - The user context object
 * @param {string} classTitle - The title for the new class
 */
const createCourseInstanceV2ProgramFlex = (userContext, classTitle) => {
  return async (dispatch, getState) => {
    const state = getState()

    // Should not create if already creating
    if (state?.lmsManager?.isCreatingProgramFlex) return

    dispatch(actions.creatingCourseInstanceV2ProgramFlex())
    try {
      // Create the class
      const response = await backoff(
        () => apiV2.createClassProgramFlex(userContext, classTitle),
        3,
      )

      // Mark the class creation as successful
      await dispatch(actions.creatingCourseInstanceV2ProgramFlexSuccess())

      // Fetch the updated instructor data to ensure Redux has the latest data
      await dispatch(fetchInstructorProgramFlexGet())

      // Return the response data so the component can use it
      return response.data
    } catch (error) {
      await dispatch(
        actions.creatingCourseInstanceV2ProgramFlexError({
          error: formatError(error),
        }),
      )
      throw error
    }
  }
}

const fetchInstructorData = () => {
  const endpoint = '/instructor/me'
  const fullUrl = createEndpoint(endpoint)
  const typesOrdered = [
    createType(actions.fetchingInstructorData.toString(), {}),
    createType(actions.fetchingInstructorDataSuccess.toString(), {}),
    createType(actions.fetchingInstructorDataError.toString(), {}),
  ]
  return get(fullUrl, typesOrdered)
}

// TODO: Remove this once we merge site selection in k8. It seems to be used for just the off flag cases of program flex. Looks like more accurate function is fetchInstructorProgramFlexGet
const fetchInstructorDataV2 = reqBody => {
  const endpoint = '/instructor/ms'
  const fullUrl = createEndpoint(endpoint, reqBody)
  const typesOrdered = [
    createType(actions.fetchingInstructorDataV2.toString(), {}),
    createType(actions.fetchingInstructorDataV2Success.toString(), {}),
    createType(actions.fetchingInstructorDataV2Error.toString(), {}),
  ]
  return get(fullUrl, typesOrdered)
}

export const createEndpoint = (endpoint, reqBody) => {
  const setV2Endpoint = !!reqBody
  const lmsUrl = setV2Endpoint ? domains.LMS_API_V2 : domains.LMS_API
  const baseApiAddress = `${lmsUrl}`
  const url = setV2Endpoint
    ? // passing a fake string as userContextB64 to avoid 502 error that occurs when passing the actual userContextB64
      // ?  `${baseApiAddress}${endpoint}?userContextB64=${encodeURIComponent(btoa(JSON.stringify(reqBody)))}`
      `${baseApiAddress}${endpoint}?userContextB64=mockUserContext`
    : `${baseApiAddress}${endpoint}`
  return url
}

const updateCourseInstance = courseInstance => {
  return async dispatch => {
    dispatch(actions.updatingCourseInstance())

    try {
      const response = await api.updateClass({
        instanceId: courseInstance.instance,
        scoId: courseInstance.scoId,
        title: courseInstance.title,
      })
      const { config } = response || {}
      const { data } = config || {}
      const updatedCourse = JSON.parse(data)
      await dispatch(
        actions.updatingCourseInstanceSuccess({
          courseInstance: updatedCourse,
        }),
      )
      return updatedCourse
    } catch (error) {
      await dispatch(
        actions.updatingCourseInstanceError({
          error: formatError(error, SHOULD_SHOW_IN_MODAL),
        }),
      )
      throw error
    }
  }
}

const updateCourseInstanceV2 = courseInstance => {
  return async dispatch => {
    dispatch(actions.updatingCourseInstance())

    try {
      const response = await apiV2.updateClass({
        registrationId: courseInstance.instanceId,
        title: courseInstance.title,
      })
      const { config } = response || {}
      const { data } = config || {}
      const updatedCourse = JSON.parse(data)
      await dispatch(
        actions.updatingCourseInstanceSuccess({
          courseInstance: updatedCourse,
        }),
      )
      return updatedCourse
    } catch (error) {
      await dispatch(
        actions.updatingCourseInstanceError({
          error: formatError(error, SHOULD_SHOW_IN_MODAL),
        }),
      )
      throw error
    }
  }
}

// TODO: Something needs to happen after we receive a success here to call fetchInstructorData
// Either create a thunk to orchestrate or create saga
const createTrainingRegistration = seedId => {
  return async dispatch => {
    dispatch(actions.creatingTrainingRegistration())
    try {
      await api.postRegistrations(seedId)

      // TODO: Temp solution until after BTS, the below comes from fecthInstructorData which maybe should be turned into a SAGA
      const lmsInstructorFetch = await api.listClassesByInstructor()
      dispatch(actions.creatingTrainingRegistrationSuccess())
      dispatch(actions.fetchingInstructorDataSuccess(lmsInstructorFetch.data))
    } catch (error) {
      dispatch(
        actions.creatingTrainingRegistrationError({
          error: formatError(error),
        }),
      )
    }
  }
}

const fetchTrainingQuestions = () => {
  return async dispatch => {
    dispatch(actions.fetchingTrainingQuestions())

    try {
      const { data } = await api.getTrainingQuestions()
      dispatch(
        actions.fetchingTrainingQuestionsSuccess({
          trainingQuestions: data,
        }),
      )
    } catch (e) {
      console.error(e)
      dispatch(
        actions.fetchingTrainingQuestionsError({ error: formatError(e) }),
      )
    }
  }
}

const deleteLessonProgressReport = () => {
  return async dispatch => {
    dispatch(actions.deletingLessonProgressReport())
  }
}

const deleteHistoricalReports = () => {
  return async dispatch => {
    dispatch(actions.deletingHistoricalReports())
  }
}

// create a new operation here that requests data from LMS and then from blob storage
const fetchHistoricalReports = licenceOrSiteId => {
  return async dispatch => {
    dispatch(actions.fetchingHistoricalReportData())
    // call lms to get a list of available reports
    try {
      const k8Json = await api.getHistoricalSiteReports(licenceOrSiteId)
      const k8Excel = await api.getK8SiteHistoricReports(licenceOrSiteId)
      const k8JsonPromises = k8Json.data.map(report =>
        fetchHistoricalReportDataFromBlobStorage(report),
      )
      const historicalK8JsonReportData = await Promise.all(k8JsonPromises)
      const historicalReportData = historicalK8JsonReportData.concat(
        k8Excel?.data || [],
      )
      dispatch(
        actions.fetchingHistoricalReportDataSuccess({
          historicalReportData,
          licenceOrSiteId,
        }),
      )
    } catch (error) {
      dispatch(actions.fetchingHistoricalReportDataError({ error }))
    }
  }
}

const extractYearFromBlobPath = (fullBlob, reportBlobType = 'leader') => {
  // fullBlob typically has structure like: "reports/2023/filename.xlsx" or "2023/reports/filename.csv"
  const pathParts = fullBlob.split('/')
  if (reportBlobType === 'leader') {
    return pathParts[2] // For leader reports, the year is typically at index 2: leader/129770/2024/k8-leader.xlsx
  } else {
    throw new Error(
      `Unsupported reportBlobType: ${reportBlobType}. Only 'leader' is supported.`,
    )
  }
}

const fetchHistoricalReportDataFromBlobStorage = async ({
  url,
  name,
  fullBlob,
}) => {
  // call blobApi to get historical report data
  try {
    const { data } = url ? await blobApi.getBlobData(url) : []
    const historicalReportData = {
      fullBlob,
      name,
      data,
    }
    return historicalReportData
  } catch (error) {
    console.error(
      'fetchHistoricalReportDataFromBlobStorage failed',
      { error },
      { url },
      { fullBlob },
    )
  }
}

const fetchK8LeaderHistoricalReports = userId => {
  return async dispatch => {
    dispatch(actions.fetchingHistoricalUserReports())
    try {
      const k8Json = await api.getK8LeaderHistoricReports(userId)
      const k8JsonPromises = k8Json.data.map(report => {
        const year = extractYearFromBlobPath(report.fullBlob)
        const filename = report.fullBlob.split('/').pop()
        const formattedName = `${year}-${filename}`
        return {
          fullBlob: report.fullBlob,
          name: formattedName,
          url: report.url,
        }
      })
      const historicalK8JsonReportData = await Promise.all(k8JsonPromises)

      dispatch(
        actions.fetchingHistoricalUserReportsSuccess({
          reports: historicalK8JsonReportData,
          program: 'K8',
        }),
      )
    } catch (error) {
      dispatch(actions.fetchingHistoricalUserReportsError({ error }))
    }
  }
}

const fetchHsLeaderHistoricalReports = userId => {
  return async dispatch => {
    dispatch(actions.fetchingHistoricalUserReports())
    try {
      const hsJson = await api.getHsLeaderHistoricReports(userId)
      const hsJsonPromises = hsJson.data.map(report => {
        const year = extractYearFromBlobPath(report.fullBlob)
        const filename = report.fullBlob.split('/').pop()
        const formattedName = `${year}-${filename}`
        return {
          fullBlob: report.fullBlob,
          name: formattedName,
          url: report.url,
        }
      })
      const historicalHsJsonReportData = await Promise.all(hsJsonPromises)

      dispatch(
        actions.fetchingHistoricalUserReportsSuccess({
          reports: historicalHsJsonReportData,
          program: 'HS',
        }),
      )
    } catch (error) {
      dispatch(actions.fetchingHistoricalUserReportsError({ error }))
    }
  }
}

const clearErrors = () => {
  return async dispatch => {
    dispatch(actions.clearErrors())
  }
}

const fetchSeedIds = () => {
  return async dispatch => {
    dispatch(actions.fetchSeedIds())
    try {
      const { data } = await api.getTrainingPaths()
      let seedIds = {}

      data?.map(item => {
        if (!item.hasOwnProperty('paths')) {
          seedIds = { ...seedIds, ...item }
        } else {
          const { paths } = item
          Object.keys(item)
            .filter(key => key !== 'paths')
            .map(key => {
              seedIds = {
                ...seedIds,
                [key]: paths?.map(path => Object.values(path)).flat(),
              }
            })
        }
      })

      dispatch(actions.fetchSeedIdsSuccess(seedIds))
    } catch (error) {
      dispatch(
        actions.fetchSeedIdsError({
          error: formatError(error),
        }),
      )
    }
  }
}

const resetState = () => {
  return async dispatch => {
    dispatch(actions.resetCourseInstances())
  }
}

export default {
  clearErrors,
  createCourseInstance,
  createCourseInstanceV2,
  createCourseInstanceV2ProgramFlex,
  createCourseInstanceByCourse,
  createFTPathInstance,
  createSelaPathInstance,
  createTrainingRegistration,
  deleteCourseInstance,
  deleteCourseInstanceV2,
  finishCourseInstanceV2,
  deletingCourseCreatedStatus,
  deleteHistoricalReports,
  deleteLessonProgressReport,
  fetchHistoricalReports,
  fetchHsLeaderHistoricalReports,
  fetchInstructorData,
  fetchInstructorDataV2,
  fetchK8LeaderHistoricalReports,
  fetchOrCreateInstructorProgramFlex,
  fetchInstructorProgramFlexGet,
  fetchSeedIds,
  fetchTrainingQuestions,
  makeAttemptV2,
  makeAttemptV2Direct,
  resetState,
  updateCourseInstance,
  updateCourseInstanceV2,
}
